import { KEYS } from 'game/constants/SceneConstants';
import { SceneBase } from '@jollywise/jollygoodgame';
import { DisplayScoreProgress } from 'game/display/DisplayScoreProgress';
import { SCORE_METRIC_TYPES, SCORE_METRIC_NAMES } from 'game/constants/Constants';
import { FONT_BASE } from 'game/constants/AppFonts';
import { trackGameEndScore } from 'game/utils/GaTracking';

const RESULT_TEXT = {
  [SCORE_METRIC_TYPES.PUBLIC_OPINION]: 'Considerate Negotiator',
  [SCORE_METRIC_TYPES.STABILITY]: 'Fair Negotiator',
  [SCORE_METRIC_TYPES.NEGOTIATION]: 'Smart Negotiator',
  [SCORE_METRIC_TYPES.CREDIBILITY]: 'Ruthless Negotiator',
  MULTIPLE: 'Natural Negotiator',
};

export class Result extends SceneBase {
  constructor() {
    super({ key: KEYS.Result, active: false });
  }
  init() {
    this.particlesList = [];
    this.playerStatus = this.game._scores.scoreHasReachedZero() ? 'fail' : 'success';
  }
  create() {
    super.create();
    this.setBackgroundColour(0xc9e3e8);
    this.bg = this.add.image(0, 0, 'backgrounds', 'background_scenario').setOrigin(0);
    this.game.hud.setState('RESULTS');

    // determine the state of this outro
    // has player succeeded or failed?
    this.scores =
      this.playerStatus === 'success'
        ? this.game._scores.getSucessScores()
        : this.game._scores.getZeroScores();

    this.playerStatus === 'success' ? this.displaySuccessUI() : this.displayFailUI();
  }

  setTrackingData(result) {
    // this.game._player.setPlayersOutcome = {
    //   scores: this.game._scores.getScores,
    //   status: this.playerStatus,
    //   result,
    // };

    trackGameEndScore({
      play_count: this.game._player.timesPlayedGame,
      scores: this.game._scores.getScores,
      result,
    });
  }

  displaySceneTitle() {
    this.add
      .text(840, 100, 'YOUR RESULTS', {
        fontFamily: FONT_BASE.CALIBRE_BOLD,
        fontSize: '68px',
        color: '#ffffff',
      })
      .setOrigin(0.5, 1);
  }

  displaySceneAide() {
    this.aide = this.add
      .image(1280, this.game.defaultHeight, 'ui', 'aide_results')
      .setOrigin(0.5, 1);
  }

  displayAddSpeechBubble(text) {
    this.speechBubble = this.add.image(840, 350, 'ui', 'reward_speech_results').setOrigin(0.5, 0);
    this.add
      .text(840, this.speechBubble.y + this.speechBubble.height / 2 - 28, text, {
        fontFamily: FONT_BASE.CALIBRE_BOLD,
        fontSize: '28px',
        color: '#000000',
        wordWrap: { width: 600 },
        align: 'center',
      })
      .setOrigin(0.5);
  }

  displayFailUI() {
    this.bg = this.add
      .image(840, 0, 'backgrounds', 'background_header_fail_results')
      .setOrigin(0.5, 0);
    this.resultsBanner = this.add
      .image(840, this.bg.height, 'ui', 'reward_bar_fail')
      .setOrigin(0.5, 1);

    this.resultsText = this.add
      .text(
        840,
        this.resultsBanner.y - this.resultsBanner.height + this.resultsBanner.height / 2,
        'You lack consideration',
        {
          fontFamily: FONT_BASE.PIXEL_OPERATOR_HB_8,
          fontSize: '32px',
          color: '#ffffff',
          wordWrap: { width: 800 },
          align: 'center',
        }
      )
      .setOrigin(0.5);

    this.resultsText.alpha = 0;

    this.displaySceneTitle();
    this.displaySceneAide();

    const text =
      this.scores.length > 1
        ? 'Multiple scores reached 0%...'
        : `Your ${SCORE_METRIC_NAMES[this.scores[0]]} score reached 0%...`;

    this.displayAddSpeechBubble(text);

    this.displayScores();

    this.setTrackingData('You lack consideration');
  }

  displaySuccessUI() {
    this.bg = this.add
      .image(840, 0, 'backgrounds', 'background_header_win_results')
      .setOrigin(0.5, 0);

    this.resultsBanner = this.add
      .image(
        840,
        this.bg.height,
        'ui',
        this.scores.length > 1
          ? 'reward_bar_credibility'
          : `reward_bar_${this.scores[0]?.replace('update_', '') || 'credibility'}`
      )
      .setOrigin(0.5, 1);

    const result_text =
      this.scores.length == 0
        ? ''
        : this.scores.length > 1
        ? RESULT_TEXT.MULTIPLE
        : RESULT_TEXT[this.scores[0]];

    this.resultsText = this.add
      .text(
        840,
        this.resultsBanner.y - this.resultsBanner.height + this.resultsBanner.height / 2,
        result_text,
        {
          fontFamily: FONT_BASE.PIXEL_OPERATOR_HB_8,
          fontSize: '32px',
          color: '#000000',
          wordWrap: { width: 800 },
          align: 'center',
        }
      )
      .setOrigin(0.5);

    this.resultsText.alpha = 0;

    this.displaySceneTitle();
    this.displaySceneAide();

    const text =
      this.scores.length == 0
        ? 'Here are your scores!'
        : this.scores.length > 1
        ? 'Scoring over 80% on multiple scores means you’re a...'
        : `Scoring over 80% on ${SCORE_METRIC_NAMES[this.scores[0]]} means you’re a...`;

    this.displayAddSpeechBubble(text);

    this.displayScores();

    this.setTrackingData(result_text);
  }

  displayScores() {
    this.scoresContainer = this.add.container(840, 115);
    this.scoresContainer.alpha = 0;

    const scores = this.game._scores.getScores;

    let count = 0;

    // loop over the object value and display the scores
    for (const [type, value] of Object.entries(scores)) {
      // phaser workout odd / even based on index
      const isEven = count % 2 === 0;

      const scoreProgress = new DisplayScoreProgress(this, {
        x: 840,
        y: 0,
        type,
        score: value,
      });

      scoreProgress.x += isEven ? scoreProgress.width / 2 + 10 : -scoreProgress.width / 2 - 10;
      if (count >= 2) {
        scoreProgress.y += scoreProgress.height + 10;
      }

      if (this.scores.indexOf(type) !== -1 && this.playerStatus === 'success') {
        this.setupParticles({
          x: this.scoresContainer.x + scoreProgress.x,
          y: this.scoresContainer.y + scoreProgress.y + scoreProgress.height / 2,
        });
      }

      this.scoresContainer.add(scoreProgress);
      count++;
    }

    this.add.tween({
      targets: [this.scoresContainer, this.resultsText],
      alpha: 1,
      delay: 100,
      duration: 300,
      ease: Phaser.Math.Easing.Back.In,
      onComplete: () => {
        this.triggerParticles();
        this.playerStatus === 'success'
          ? this.game.soundController.playSFX('sfx', 'chn_success_end')
          : this.game.soundController.playSFX('sfx', 'chn_fail_end');
      },
    });
  }

  setupParticles({ x, y }) {
    const particleEmitter = this.add.particles(x, y, 'ui', {
      frame: ['star_large', 'star_small'],
      lifespan: 1000,
      speed: 200,
      scale: { start: 0.8, end: 0 },
      emitting: false,
    });

    this.particlesList.push(particleEmitter);
  }

  triggerParticles() {
    this.particlesList.map((particle) => {
      particle.explode(8);
    });
  }

  shutdown() {
    super.shutdown();
  }
}
